/**
 * Zod schemas for the Campaign Builder feature.
 *
 * Owned by `prompts/02-features/campaign-builder/01-campaign-planning-ai-opus-4.7.md`.
 *
 * Two surfaces share these schemas:
 *  - tRPC router input validation (`apps/api/src/routers/campaign.ts`)
 *  - AI plan output validation in the planner service
 *
 * The DB enums (campaignStatusEnum, campaignItemTypeEnum,
 * campaignItemStatusEnum, platformEnum) are the source of truth for
 * persistable values. The `campaignContentTypeHintSchema` below is a richer
 * planning-only set used for AI prompt briefs; it is mapped down to the DB
 * enum at persist time via `toDbItemType()`.
 */

import { z } from "zod";

// ---------------------------------------------------------------------------
// Platform / status enums (mirror DB)
// ---------------------------------------------------------------------------

export const campaignPlatformSchema = z.enum([
  "instagram",
  "tiktok",
  "youtube",
  "linkedin",
  "twitter",
  "threads",
]);
export type CampaignPlatform = z.infer<typeof campaignPlatformSchema>;

export const campaignTemplateSchema = z.enum([
  "product_launch",
  "event",
  "series",
  "custom",
]);
export type CampaignTemplateType = z.infer<typeof campaignTemplateSchema>;

/**
 * Persistable campaign status values — mirrors campaignStatusEnum in
 * packages/db/src/enums.ts. The wider state vocabulary in the prompt
 * (draft, planned, scheduled, cancelled) collapses down to these five.
 */
export const campaignStatusSchema = z.enum([
  "planning",
  "producing",
  "scheduled",
  "live",
  "complete",
  "failed",
]);
export type CampaignStatus = z.infer<typeof campaignStatusSchema>;

/**
 * Persistable campaign-item type — mirrors campaignItemTypeEnum.
 * Note: `clip` covers video_clip / reel; richer hints from the AI plan
 * (story / thread / article) are mapped onto these four buckets via
 * `toDbItemType()` in the service layer.
 */
export const campaignItemTypeSchema = z.enum([
  "carousel",
  "clip",
  "post",
  "video_ad",
]);
export type CampaignItemType = z.infer<typeof campaignItemTypeSchema>;

export const campaignItemStatusSchema = z.enum([
  "pending",
  "generating",
  "generated",
  "approved",
  "scheduled",
  "complete",
  "failed",
]);
export type CampaignItemStatus = z.infer<typeof campaignItemStatusSchema>;

/**
 * Richer hint vocabulary used inside AI plans only. The planner emits one
 * of these so the brief reads naturally to a content producer; the service
 * layer normalises the hint to a DB-persistable `CampaignItemType` before
 * insert.
 */
export const campaignContentTypeHintSchema = z.enum([
  "carousel",
  "video_clip",
  "post",
  "video_ad",
  "story",
  "reel",
  "thread",
  "article",
]);
export type CampaignContentTypeHint = z.infer<
  typeof campaignContentTypeHintSchema
>;

export const narrativeArcSchema = z.enum([
  "intro",
  "build",
  "climax",
  "resolution",
]);
export type NarrativeArc = z.infer<typeof narrativeArcSchema>;

// ---------------------------------------------------------------------------
// Plan item — produced by the AI planner, validated, then persisted
// ---------------------------------------------------------------------------

export const campaignPlanItemSchema = z.object({
  /** 1-based day within the campaign (1..durationDays). */
  day: z.number().int().min(1),
  platform: campaignPlatformSchema,
  contentType: campaignContentTypeHintSchema,
  /** Free-form brief — what to make and why; up to 2000 chars. */
  brief: z.string().min(10).max(2000),
  /** Pointer back into the long-form source content this piece derives from. */
  sourceContent: z.string().max(2000).default(""),
  /**
   * IDs of other plan items this depends on. The planner emits stable
   * synthetic IDs of the form `day-{day}-{platform}-{idx}`; the service
   * layer rewrites them to real `campaign_items.id` values after insert.
   */
  dependencies: z.array(z.string()).default([]),
  narrativeArc: narrativeArcSchema,
  hashtags: z.array(z.string().max(64)).max(30).default([]),
  estimatedCredits: z.number().int().min(0).default(0),
  /** Suggested ISO-8601 datetime; consumers may localise to workspace tz. */
  scheduledTime: z.string().datetime().optional(),
});
export type CampaignPlanItem = z.infer<typeof campaignPlanItemSchema>;

export const campaignPlanSchema = z.object({
  items: z.array(campaignPlanItemSchema).min(1).max(500),
  narrativeSummary: z.string().min(20).max(2000),
});
export type CampaignPlan = z.infer<typeof campaignPlanSchema>;

// ---------------------------------------------------------------------------
// Router input schemas — mirror prompt task §1
// ---------------------------------------------------------------------------

const dayOfWeekSchema = z.enum([
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
]);

export const postingPreferencesSchema = z
  .object({
    timezone: z.string().min(1).max(64).default("UTC"),
    preferredTimes: z
      .record(campaignPlatformSchema, z.array(z.string()))
      .optional(),
    excludeDays: z.array(dayOfWeekSchema).optional(),
  })
  .strict();
export type PostingPreferences = z.infer<typeof postingPreferencesSchema>;

export const createCampaignSchema = z
  .object({
    workspaceId: z.string().min(1),
    name: z.string().min(1).max(200),
    goal: z.string().min(10).max(2000),
    durationDays: z.number().int().min(7).max(90),
    templateType: campaignTemplateSchema,
    platforms: z.array(campaignPlatformSchema).min(1).max(6),
    brandProfileId: z.string().min(1),
    longFormContent: z.string().min(0).max(100_000).optional(),
    postingPreferences: postingPreferencesSchema.optional(),
  })
  .strict();
export type CreateCampaignInput = z.infer<typeof createCampaignSchema>;

export const planCampaignSchema = z.object({
  campaignId: z.string().min(1),
});

export const updateCampaignItemSchema = z
  .object({
    itemId: z.string().min(1),
    changes: z
      .object({
        platform: campaignPlatformSchema.optional(),
        contentType: campaignItemTypeSchema.optional(),
        scheduledDay: z.number().int().min(1).optional(),
        status: campaignItemStatusSchema.optional(),
      })
      .strict()
      .refine(
        (changes) => Object.keys(changes).length > 0,
        "at least one change is required",
      ),
  })
  .strict();
export type UpdateCampaignItemInput = z.infer<typeof updateCampaignItemSchema>;

export const getCampaignProgressSchema = z.object({
  campaignId: z.string().min(1),
});

export const listCampaignsSchema = z
  .object({
    workspaceId: z.string().min(1),
    status: campaignStatusSchema.optional(),
    templateType: campaignTemplateSchema.optional(),
    limit: z.number().int().min(1).max(50).default(20),
    cursor: z.string().min(1).optional(),
  })
  .strict();
export type ListCampaignsInput = z.infer<typeof listCampaignsSchema>;

export const deleteCampaignSchema = z.object({
  campaignId: z.string().min(1),
});

// Production lifecycle (prompt 03 — batch orchestration & progress)
export const startCampaignProductionSchema = z.object({
  campaignId: z.string().min(1),
});
export type StartCampaignProductionInput = z.infer<
  typeof startCampaignProductionSchema
>;

export const retryFailedCampaignItemsSchema = z.object({
  campaignId: z.string().min(1),
});
export type RetryFailedCampaignItemsInput = z.infer<
  typeof retryFailedCampaignItemsSchema
>;

export const retryCampaignItemSchema = z.object({
  itemId: z.string().min(1),
});
export type RetryCampaignItemInput = z.infer<typeof retryCampaignItemSchema>;

export const getCampaignCostBreakdownSchema = z.object({
  campaignId: z.string().min(1),
});

// ---------------------------------------------------------------------------
// Mapping helpers
// ---------------------------------------------------------------------------

/**
 * Map a richer planner hint onto the four DB-persistable item types.
 * Story / reel collapse to `clip`; thread / article collapse to `post`.
 */
export function toDbItemType(hint: CampaignContentTypeHint): CampaignItemType {
  switch (hint) {
    case "carousel":
      return "carousel";
    case "video_clip":
    case "reel":
    case "story":
      return "clip";
    case "video_ad":
      return "video_ad";
    case "post":
    case "thread":
    case "article":
      return "post";
  }
}
